<script>
import BarChart from "@/components/bar-chart.vue";
import PieChart from "@/components/pie-chart.vue";
import { toCurrency } from "@/helpers/toCurrency";
import { ChartColorsEnum, CHART_COLORS } from "./constants";
import Table from "./shared/bp-table.vue";
import { TabEnum } from "../impairment/form/impairment.types";
import moment from "moment";
import { mapGetters } from "vuex";

/**
 * @typedef {Object} PercentualStatus
 * @property {number} em_amortizacao
 * @property {number} em_desenvolvimento
 */

/**
 * @typedef {Object} ValorDiretoria
 * @property {string} diretoria
 * @property {number} inicial
 * @property {number} impairment
 * @property {number} revisado
 */

/**
 * @typedef {Object} ProjetoFinalizado
 * @property {number} business_plan_id
 * @property {string} nome_iniciativa
 * @property {string} data_fim
 * @property {string} valor_ativo
 * @property {string} vpl
 * @property {string} teste_impairment_data
 * @property {string} teste_impairment_status_label_label
 * @property {string} status
 */

/**
 * @typedef {Object} ProjetoAndamento
 * @property {number} business_plan_id
 * @property {string} nome_iniciativa
 * @property {string} status_execucao
 * @property {string} vpl_atual
 * @property {string} teste_impairment_data
 * @property {string} vpl_status
 * @property {boolean} vpl_reduzido
 */

/**
 * @typedef {Object} DashboardImpairment
 * @property {number} em_revisao
 * @property {number} aguardando
 * @property {number} em_conformidade
 * @property {number} em_ajuste_contabil
 * @property {PercentualStatus} percentual_status
 * @property {ValorDiretoria} valor_diretorias
 * @property {ProjetoFinalizado[]} projetos_finalizados
 * @property {ProjetoAndamento[]} projetos_andamento
 */

/**
 * @function createEmptyData
 * @returns {DashboardImpairment}
 */
const createEmptyData = () => ({
  aguardando: 0,
  em_ajuste_contabil: 0,
  em_conformidade: 0,
  em_revisao: 0,
  percentual_status: {
    em_amortizacao: 0,
    em_desenvolvimento: 0
  },
  projetos_andamento: [],
  projetos_finalizados: [],
  valor_diretorias: []
});

const parseNumber = number =>
  parseFloat(number).toLocaleString("pt-br", {
    maximumFractionDigits: 2
  });

export default {
  components: {
    BarChart,
    PieChart,
    Table
  },
  data() {
    return {
      data: createEmptyData()
    };
  },
  computed: {
    ...mapGetters(["clientId"]),
    columns() {
      const andamento = [
        {
          text: "",
          value: "vpl_status",
          width: 54
        },
        {
          text: "Nome do projeto",
          value: "nome_iniciativa"
        },
        {
          text: "Status de execução",
          value: "status_execucao"
        },
        {
          text: "VPL atual",
          value: "vpl_atual",
          align: "end",
          format(value) {
            return value ? toCurrency(value) : `R$ 0,00`;
          }
        },
        {
          text: "Último teste de impairment",
          value: "teste_impairment_data",
          align: "end",
          format(value) {
            const date = moment(value);
            return date.isValid() ? date.format("DD/MM/YYYY") : value;
          }
        }
      ];
      const finalizado = [
        {
          text: "",
          value: "status",
          width: 54
        },
        {
          text: "Nome do projeto",
          value: "nome_iniciativa"
        },
        {
          text: "Data de fim",
          value: "data_fim",
          align: "end",
          format(value) {
            const date = moment(value);
            return date.isValid() ? date.format("DD/MM/YYYY") : value;
          }
        },
        {
          text: "Valor do ativo",
          value: "valor_ativo",
          align: "end",
          format(value) {
            return value ? toCurrency(value) : `R$ 0,00`;
          }
        },
        {
          text: "VPL",
          value: "vpl",
          align: "end",
          format(value) {
            return value ? toCurrency(value) : `R$ 0,00`;
          }
        },
        {
          text: "Última data de teste de impairment",
          value: "teste_impairment_data",
          align: "end",
          format(value) {
            const date = moment(value);
            return date.isValid() ? date.format("DD/MM/YYYY") : value;
          }
        },
        {
          text: "Status do teste",
          value: "teste_impairment_status_label"
        }
      ];

      return {
        andamento,
        finalizado
      };
    },    
    percentual() {
      const {
        em_amortizacao,
        em_desenvolvimento
      } = this.data.percentual_status;
      const data = [em_amortizacao, em_desenvolvimento];
      const chartdata = {
        labels: ["Em amortização", "Em desenvolvimento"],
        datasets: [
          {
            backgroundColor: [
              ChartColorsEnum.darkestBlue,
              ChartColorsEnum.darkBlue_25
            ],
            data,
            label: "Pizza"
          }
        ]
      };
      const options = {
        cutoutPercentage: 50,
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: "right"
        },
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) => {
              const percent = parseNumber(
                data.datasets[0].data[tooltipItem.index]
              );
              return `${percent}%`;
            }
          }
        }
      };

      return { chartdata, options };
    },
    valorDiretorias() {
      /** @type {ValorDiretoria[]} */
      const data = this.data.valor_diretorias;
      const totalInicial = data.reduce((acc, { inicial }) => acc + inicial, 0);
      const totalRevisado = data.reduce(
        (acc, { revisado }) => acc + revisado,
        0
      );
      let inicialStart = 0;
      let impairmentStart = Math.min(totalInicial, totalRevisado);
      let revisadoStart = 0;
      const chartdata = {
        labels: ["Ativo inicial", "Impairment", "Ativo revisado"],
        datasets: data.map(
          ({ diretoria, impairment, inicial, revisado }, i) => {
            const data = [
              [inicialStart, inicialStart + inicial],
              [impairmentStart, impairmentStart + impairment],
              [revisadoStart, revisadoStart + revisado]
            ];
            inicialStart += inicial;
            impairmentStart += impairment;
            revisadoStart += revisado;

            return {
              label: diretoria,
              data,
              backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
              borderColor: CHART_COLORS[i % CHART_COLORS.length],
              borderWidth: 1
            };
          }
        )
      };
      const options = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          xAxes: [
            {
              stacked: true
            }
          ]
        },
        legend: {
          position: "top"
        },
        tooltips: {
          mode: "index",
          callbacks: {
            label(tooltipItem, data) {
              const ref = data.datasets[tooltipItem.datasetIndex];
              let label = ref.label || "";

              if (label) {
                label += ": ";
              }

              if (tooltipItem?.value !== null) {
                label += new Intl.NumberFormat("pt-BR").format(
                  tooltipItem.value
                );
              }

              return label;
            },
            afterBody(tooltipItem) {
              if (tooltipItem?.length > 1) {
                const total = tooltipItem.reduce(
                  (sum, { value }) => sum + value * 1,
                  0
                );
                return [
                  "Total: " + new Intl.NumberFormat("pt-BR").format(total)
                ];
              }
            }
          }
        }
      };

      return { chartdata, options };
    }
  },
  created() {
    if (this.clientId === 0) {
      this.goToHome();
    } else {
      this.doLoad();
    }
  },
  watch: {
    clientId(value) {
      if (value === 0) {
        this.goToHome();
      } else {
        this.doLoad();
      }
    }
  },
  methods: {
    async doLoad() {
      try {
        const impairment = await this.getImpairment();
        const andamento = await this.getAndamento();
        const finalizado = await this.getAmortizacao();

        this.data = {
          ...impairment,
          projetos_andamento: andamento.data,
          projetos_finalizados: finalizado.data
        };
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }

        this.data = createEmptyData();
      }
    },
    getAndamentoIconName({ vpl_status, vpl_reduzido }) {
      if (vpl_reduzido) {
        return "mdi-alert-circle-outline";
      }

      if (vpl_status) {
        return "mdi-circle";
      }

      return "mdi-circle-off-outline";
    },
    getAndamentoIconColor({ vpl_status }) {
      switch (vpl_status) {
        case "negativo":
          return "red";
        case "finalizara":
          return "yellow";
        case "positivo":
          return "green";
        default:
          return "inherit";
      }
    },
    getAndamentoIconTooltip({ vpl_status, vpl_reduzido }) {
      let label = "";

      switch (vpl_status) {
        case "negativo":
          label = "VPL negativo.";
          break;
        case "finalizara":
          label = "Finalizará em 3 meses ou menos.";
          break;
        case "positivo":
          label = "VPL positivo.";
          break;
      }

      if (vpl_reduzido) {
        label += " VPL ao menos 10% abaixo do projetado.";
      }

      return label;
    },
    getFinalizadoIconName(status) {
      if (status === "necessita_realizar_teste") {
        return "mdi-alert";
      }

      if (status) {
        return "mdi-circle";
      }

      return "mdi-circle-off-outline";
    },
    getFinalizadoIconColor(status) {
      switch (status) {
        case "necessita_revisao":
        case "necessita_realizar_teste":
          return "red";
        case "aguardando_documento":
          return "yellow";
        case "teste_concluido":
          return "green";
        default:
          return "inherit";
      }
    },
    getFinalizadoIconTooltip(status) {
      switch (status) {
        case "necessita_revisao":
          return "Necessita refazer teste";
        case "necessita_realizar_teste":
          return "Necessita realizar teste";
        case "aguardando_documento":
          return "Aguardando documento";
        case "teste_concluido":
          return "Teste concluído";
        default:
          return "";
      }
    },
    getItemClass(row) {
      return {
        clickable: row.status !== "necessita_realizar_teste"
      };
    },
    goToHome() {
      this.notify({
        error:
          "Não é possível acessar o módulo de Ativação P&D sem uma empresa selecionada."
      });
      this.$router.push({ name: "homepage" });
    },
    onClickRow(row) {
      this.$router.push({
        name: "ativacaoped-impairment-form",
        params: { id: row.business_plan_id },
        query: { tab: TabEnum.MEMORIA_EVIDENCIAS }
      });
    },
    async getAndamento() {
      const { get } = this.apiResource(`v1/ativacaoped/${this.clientId}/dashboard/impairment/andamento`);
      return get();
    },
    async getAmortizacao() {
      const { get } = this.apiResource(`v1/ativacaoped/${this.clientId}/dashboard/impairment/amortizacao`);
      return get();
    },
    async getImpairment() {
      const { get } = this.apiResource(`v1/ativacaoped/${this.clientId}/dashboard/impairment`);
      return get();
    }
  }
};
</script>

<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <h1>Gestão de projetos - CAPEX</h1>
        <span>Impairment</span>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6" lg="3" class="data-values">
        <v-card>
          <span> {{ data.em_revisao }} em revisão </span>
        </v-card>
      </v-col>
      <v-col cols="12" md="6" lg="3" class="data-values">
        <v-card>
          <span> {{ data.aguardando }} aguardando documentação </span>
        </v-card>
      </v-col>
      <v-col cols="12" md="6" lg="3" class="data-values">
        <v-card>
          <span> {{ data.em_conformidade }} em revisão </span>
        </v-card>
      </v-col>
      <v-col cols="12" md="6" lg="3" class="data-values">
        <v-card>
          <span> {{ data.em_ajuste_contabil }} em ajuste contábil </span>
        </v-card>
      </v-col>
      <v-col cols="12" lg="4">
        <v-card class="px-5">
          <PieChart v-bind="percentual"></PieChart>
        </v-card>
      </v-col>
      <v-col cols="12" lg="8">
        <v-card class="px-5">
          <BarChart v-bind="valorDiretorias"></BarChart>
        </v-card>
      </v-col>

      <v-col cols="12">
        <v-card color="#81B4EA">
          <v-card-title>Projetos finalizados</v-card-title>
          <Table
            class="projetos-finalizados"
            :columns="columns.finalizado"
            :itemClass="getItemClass"
            :rows="data.projetos_finalizados"
            @click:row="onClickRow"
          >
            <template v-slot:[`item.status`]="{ value }">
              <v-tooltip right :disabled="!getFinalizadoIconTooltip(value)">
                <template #activator="{ on }">
                  <v-icon v-on="on" :color="getFinalizadoIconColor(value)">
                    {{ getFinalizadoIconName(value) }}
                  </v-icon>
                </template>

                {{ getFinalizadoIconTooltip(value) }}
              </v-tooltip>
            </template>
          </Table>
        </v-card>
      </v-col>
      <v-col cols="12">
        <v-card color="#81B4EA">
          <v-card-title>Projetos em andamento</v-card-title>
          <Table
            class="projetos-andamento"
            :columns="columns.andamento"
            :rows="data.projetos_andamento"
            @click:row="onClickRow"
          >
            <template v-slot:[`item.vpl_status`]="{ item }">
              <v-tooltip right :disabled="!getAndamentoIconTooltip(item)">
                <template #activator="{ on }">
                  <v-icon v-on="on" :color="getAndamentoIconColor(item)">
                    {{ getAndamentoIconName(item) }}
                  </v-icon>
                </template>

                {{ getAndamentoIconTooltip(item) }}
              </v-tooltip>
            </template>
          </Table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped lang="scss">
.v-card__title {
  word-break: normal !important;
}

.data-values {
  display: flex;

  .v-card {
    flex-grow: 1;
    padding: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;

    span {
      font-size: 1.5rem;
    }
  }
}

.projetos-finalizados::v-deep .clickable {
  cursor: pointer;
}

.projetos-andamento::v-deep tr {
  cursor: pointer;
}
</style>
